import { memo, useCallback, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, Shield, Zap, Users, BarChart3, Target, Calendar, Layers, Play, Building2, DollarSign } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { useScrollAnimation, useStaggeredAnimation } from '@/hooks/useScrollAnimation';
import { OptimizedImage } from '@/components/ui/optimized-image';
import { AnimatedCard } from '@/components/ui/animated-card';
import WaitlistModal from '@/components/WaitlistModal';

const CopyTradeSection = memo(() => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<string>('');
  
  const headerAnimation = useScrollAnimation({ threshold: 0.3 });
  const benefitsAnimation = useScrollAnimation({ threshold: 0.2 });
  const statsAnimation = useScrollAnimation({ threshold: 0.2 });
  const plansHeaderAnimation = useScrollAnimation({ threshold: 0.3 });

  const handlePlanSelect = (planName: string) => {
    setSelectedPlan(planName);
    setIsModalOpen(true);
  };
  
  const realPerformanceData = [
    { date: '02/01/24', value: 201.38, month: 'Jan' },
    { date: '03/01/24', value: 275.77, month: 'Jan' },
    { date: '04/01/24', value: 507.35, month: 'Jan' },
    { date: '05/01/24', value: 913.99, month: 'Jan' },
    { date: '08/01/24', value: 969.20, month: 'Jan' },
    { date: '09/01/24', value: 1024.41, month: 'Jan' },
    { date: '10/01/24', value: 1442.29, month: 'Jan' },
    { date: '11/01/24', value: 1697.82, month: 'Jan' },
    { date: '12/01/24', value: 1705.96, month: 'Jan' },
    { date: '15/01/24', value: 1916.51, month: 'Jan' },
    { date: '16/01/24', value: 1925.87, month: 'Jan' },
    { date: '17/01/24', value: 1934.76, month: 'Jan' },
    { date: '18/01/24', value: 2085.19, month: 'Jan' },
    { date: '19/01/24', value: 1804.58, month: 'Jan' },
    { date: '22/01/24', value: 1561.63, month: 'Jan' },
    { date: '23/01/24', value: 1551.22, month: 'Jan' },
    { date: '24/01/24', value: 1450.69, month: 'Jan' },
    { date: '25/01/24', value: 1615.58, month: 'Jan' },
    { date: '26/01/24', value: 1536.01, month: 'Jan' },
    { date: '29/01/24', value: 1355.59, month: 'Jan' },
    { date: '30/01/24', value: 1750.76, month: 'Jan' },
    { date: '31/01/24', value: 1807.64, month: 'Jan' },
    { date: '01/02/24', value: 1923.19, month: 'Fev' },
    { date: '02/02/24', value: 1740.28, month: 'Fev' },
    { date: '05/02/24', value: 1733.44, month: 'Fev' },
    { date: '06/02/24', value: 1857.66, month: 'Fev' },
    { date: '07/02/24', value: 1729.50, month: 'Fev' },
    { date: '08/02/24', value: 1906.68, month: 'Fev' },
    { date: '09/02/24', value: 1888.59, month: 'Fev' },
    { date: '14/02/24', value: 1932.30, month: 'Fev' },
    { date: '15/02/24', value: 1913.99, month: 'Fev' },
    { date: '16/02/24', value: 2386.49, month: 'Fev' },
    { date: '19/02/24', value: 2485.83, month: 'Fev' },
    { date: '20/02/24', value: 2376.32, month: 'Fev' },
    { date: '21/02/24', value: 2642.87, month: 'Fev' },
    { date: '22/02/24', value: 2500.74, month: 'Fev' },
    { date: '23/02/24', value: 2644.56, month: 'Fev' },
    { date: '26/02/24', value: 2354.66, month: 'Fev' },
    { date: '27/02/24', value: 2191.06, month: 'Fev' },
    { date: '28/02/24', value: 2332.47, month: 'Fev' },
    { date: '29/02/24', value: 2582.21, month: 'Fev' },
    { date: '01/03/24', value: 2718.52, month: 'Mar' },
    { date: '04/03/24', value: 2797.43, month: 'Mar' },
    { date: '05/03/24', value: 2839.25, month: 'Mar' },
    { date: '06/03/24', value: 2645.58, month: 'Mar' },
    { date: '07/03/24', value: 2603.65, month: 'Mar' },
    { date: '08/03/24', value: 2613.56, month: 'Mar' },
    { date: '11/03/24', value: 2927.03, month: 'Mar' },
    { date: '12/03/24', value: 3097.79, month: 'Mar' },
    { date: '13/03/24', value: 2847.22, month: 'Mar' },
    { date: '14/03/24', value: 3014.08, month: 'Mar' },
    { date: '15/03/24', value: 3039.10, month: 'Mar' },
    { date: '18/03/24', value: 3005.76, month: 'Mar' },
    { date: '19/03/24', value: 3230.13, month: 'Mar' },
    { date: '20/03/24', value: 3538.37, month: 'Mar' },
    { date: '21/03/24', value: 3826.66, month: 'Mar' },
    { date: '22/03/24', value: 3760.86, month: 'Mar' },
    { date: '25/03/24', value: 3801.06, month: 'Mar' },
    { date: '26/03/24', value: 3969.35, month: 'Mar' },
    { date: '27/03/24', value: 4266.50, month: 'Mar' },
    { date: '28/03/24', value: 4272.70, month: 'Mar' },
    { date: '01/04/24', value: 4337.61, month: 'Abr' },
    { date: '02/04/24', value: 4218.38, month: 'Abr' },
    { date: '03/04/24', value: 4081.18, month: 'Abr' },
    { date: '04/04/24', value: 4345.72, month: 'Abr' },
    { date: '05/04/24', value: 4719.01, month: 'Abr' },
    { date: '08/04/24', value: 4806.65, month: 'Abr' },
    { date: '09/04/24', value: 5109.39, month: 'Abr' },
    { date: '10/04/24', value: 5283.76, month: 'Abr' },
    { date: '11/04/24', value: 5256.77, month: 'Abr' },
    { date: '12/04/24', value: 5431.09, month: 'Abr' },
    { date: '15/04/24', value: 5840.74, month: 'Abr' },
    { date: '16/04/24', value: 5935.61, month: 'Abr' },
    { date: '17/04/24', value: 6350.58, month: 'Abr' },
    { date: '18/04/24', value: 5928.67, month: 'Abr' },
    { date: '19/04/24', value: 6195.09, month: 'Abr' },
    { date: '22/04/24', value: 6314.54, month: 'Abr' },
    { date: '23/04/24', value: 6356.78, month: 'Abr' },
    { date: '24/04/24', value: 6477.17, month: 'Abr' },
    { date: '25/04/24', value: 6181.69, month: 'Abr' },
    { date: '26/04/24', value: 6239.80, month: 'Abr' },
    { date: '29/04/24', value: 6413.26, month: 'Abr' },
    { date: '30/04/24', value: 6810.88, month: 'Abr' },
    { date: '02/05/24', value: 6809.26, month: 'Mai' },
    { date: '03/05/24', value: 6749.61, month: 'Mai' },
    { date: '06/05/24', value: 6751.29, month: 'Mai' },
    { date: '07/05/24', value: 7036.41, month: 'Mai' },
    { date: '08/05/24', value: 7204.20, month: 'Mai' },
    { date: '09/05/24', value: 7200.29, month: 'Mai' },
    { date: '10/05/24', value: 7404.98, month: 'Mai' },
    { date: '13/05/24', value: 7526.44, month: 'Mai' },
    { date: '14/05/24', value: 7822.20, month: 'Mai' },
    { date: '15/05/24', value: 7783.83, month: 'Mai' },
    { date: '16/05/24', value: 7820.34, month: 'Mai' },
    { date: '17/05/24', value: 7843.96, month: 'Mai' },
    { date: '20/05/24', value: 7653.29, month: 'Mai' },
    { date: '21/05/24', value: 7814.56, month: 'Mai' },
    { date: '22/05/24', value: 7968.81, month: 'Mai' },
    { date: '23/05/24', value: 8071.87, month: 'Mai' },
    { date: '24/05/24', value: 8126.99, month: 'Mai' },
    { date: '27/05/24', value: 7945.96, month: 'Mai' },
    { date: '28/05/24', value: 7963.87, month: 'Mai' },
    { date: '29/05/24', value: 7997.36, month: 'Mai' },
    { date: '31/05/24', value: 7938.95, month: 'Mai' },
    { date: '03/06/24', value: 8008.73, month: 'Jun' },
    { date: '04/06/24', value: 8191.58, month: 'Jun' },
    { date: '05/06/24', value: 8670.85, month: 'Jun' },
    { date: '06/06/24', value: 8807.81, month: 'Jun' },
    { date: '07/06/24', value: 8961.36, month: 'Jun' },
    { date: '10/06/24', value: 9048.51, month: 'Jun' },
    { date: '11/06/24', value: 8766.79, month: 'Jun' },
    { date: '12/06/24', value: 8863.53, month: 'Jun' },
    { date: '13/06/24', value: 8977.62, month: 'Jun' },
    { date: '14/06/24', value: 9572.30, month: 'Jun' },
    { date: '17/06/24', value: 9635.87, month: 'Jun' },
    { date: '18/06/24', value: 9798.22, month: 'Jun' },
    { date: '19/06/24', value: 9893.32, month: 'Jun' },
    { date: '20/06/24', value: 9761.62, month: 'Jun' },
    { date: '21/06/24', value: 10092.22, month: 'Jun' },
    { date: '24/06/24', value: 10344.65, month: 'Jun' },
    { date: '25/06/24', value: 10604.89, month: 'Jun' },
    { date: '26/06/24', value: 10525.06, month: 'Jun' },
    { date: '27/06/24', value: 10907.65, month: 'Jun' },
    { date: '28/06/24', value: 10729.32, month: 'Jun' },
    { date: '01/07/24', value: 10948.73, month: 'Jul' },
    { date: '02/07/24', value: 11488.86, month: 'Jul' },
    { date: '03/07/24', value: 11392.80, month: 'Jul' },
    { date: '04/07/24', value: 11381.58, month: 'Jul' },
    { date: '05/07/24', value: 11503.55, month: 'Jul' },
    { date: '08/07/24', value: 11504.89, month: 'Jul' },
    { date: '09/07/24', value: 11296.80, month: 'Jul' },
    { date: '10/07/24', value: 11412.55, month: 'Jul' },
    { date: '11/07/24', value: 11302.13, month: 'Jul' },
    { date: '12/07/24', value: 11498.88, month: 'Jul' },
    { date: '15/07/24', value: 11417.04, month: 'Jul' },
    { date: '16/07/24', value: 11829.06, month: 'Jul' },
    { date: '17/07/24', value: 11774.45, month: 'Jul' },
    { date: '18/07/24', value: 11812.08, month: 'Jul' },
    { date: '19/07/24', value: 12076.82, month: 'Jul' },
    { date: '22/07/24', value: 11932.69, month: 'Jul' },
    { date: '23/07/24', value: 12080.22, month: 'Jul' },
    { date: '24/07/24', value: 12443.69, month: 'Jul' },
    { date: '25/07/24', value: 12224.23, month: 'Jul' },
    { date: '26/07/24', value: 12363.20, month: 'Jul' },
    { date: '29/07/24', value: 12517.21, month: 'Jul' },
    { date: '30/07/24', value: 12775.62, month: 'Jul' },
    { date: '31/07/24', value: 12630.26, month: 'Jul' },
    { date: '01/08/24', value: 12468.21, month: 'Ago' },
    { date: '02/08/24', value: 12674.64, month: 'Ago' },
    { date: '05/08/24', value: 12836.08, month: 'Ago' },
    { date: '06/08/24', value: 12988.21, month: 'Ago' },
    { date: '07/08/24', value: 13159.54, month: 'Ago' },
    { date: '08/08/24', value: 13125.58, month: 'Ago' },
    { date: '09/08/24', value: 13274.07, month: 'Ago' },
    { date: '12/08/24', value: 13434.72, month: 'Ago' },
    { date: '13/08/24', value: 13393.89, month: 'Ago' },
    { date: '14/08/24', value: 13869.08, month: 'Ago' },
    { date: '15/08/24', value: 14065.89, month: 'Ago' },
    { date: '16/08/24', value: 13929.67, month: 'Ago' },
    { date: '19/08/24', value: 14163.02, month: 'Ago' },
    { date: '20/08/24', value: 14070.12, month: 'Ago' },
    { date: '21/08/24', value: 14329.58, month: 'Ago' },
    { date: '22/08/24', value: 14663.34, month: 'Ago' },
    { date: '23/08/24', value: 14601.24, month: 'Ago' },
    { date: '26/08/24', value: 14895.95, month: 'Ago' },
    { date: '27/08/24', value: 15080.55, month: 'Ago' },
    { date: '28/08/24', value: 15347.00, month: 'Ago' },
    { date: '29/08/24', value: 15828.40, month: 'Ago' },
    { date: '30/08/24', value: 15881.36, month: 'Ago' },
    { date: '02/09/24', value: 15832.65, month: 'Set' },
    { date: '03/09/24', value: 15756.79, month: 'Set' },
    { date: '04/09/24', value: 15695.66, month: 'Set' },
    { date: '05/09/24', value: 15782.28, month: 'Set' },
    { date: '06/09/24', value: 15952.55, month: 'Set' },
    { date: '09/09/24', value: 16109.93, month: 'Set' },
    { date: '10/09/24', value: 16377.41, month: 'Set' },
    { date: '11/09/24', value: 16482.05, month: 'Set' },
    { date: '12/09/24', value: 16874.79, month: 'Set' },
    { date: '13/09/24', value: 16923.90, month: 'Set' },
    { date: '16/09/24', value: 17569.97, month: 'Set' },
    { date: '17/09/24', value: 17797.15, month: 'Set' },
    { date: '18/09/24', value: 17727.75, month: 'Set' },
    { date: '19/09/24', value: 17615.61, month: 'Set' },
    { date: '20/09/24', value: 17814.15, month: 'Set' },
    { date: '23/09/24', value: 17871.49, month: 'Set' },
    { date: '24/09/24', value: 18116.33, month: 'Set' },
    { date: '25/09/24', value: 18313.02, month: 'Set' },
    { date: '26/09/24', value: 18400.49, month: 'Set' },
    { date: '27/09/24', value: 18333.17, month: 'Set' },
    { date: '30/09/24', value: 18132.24, month: 'Set' },
    { date: '01/10/24', value: 18144.98, month: 'Out' },
    { date: '02/10/24', value: 18418.30, month: 'Out' },
    { date: '03/10/24', value: 18563.16, month: 'Out' },
    { date: '04/10/24', value: 18416.05, month: 'Out' },
    { date: '07/10/24', value: 18552.72, month: 'Out' },
    { date: '08/10/24', value: 18731.83, month: 'Out' },
    { date: '09/10/24', value: 18657.09, month: 'Out' },
    { date: '10/10/24', value: 18789.88, month: 'Out' },
    { date: '11/10/24', value: 18903.56, month: 'Out' },
    { date: '14/10/24', value: 18777.00, month: 'Out' },
    { date: '15/10/24', value: 18950.60, month: 'Out' },
    { date: '16/10/24', value: 19164.80, month: 'Out' },
    { date: '17/10/24', value: 19483.45, month: 'Out' },
    { date: '18/10/24', value: 19796.25, month: 'Out' },
    { date: '21/10/24', value: 19622.75, month: 'Out' },
    { date: '22/10/24', value: 19537.23, month: 'Out' },
    { date: '23/10/24', value: 19742.27, month: 'Out' },
    { date: '24/10/24', value: 19947.07, month: 'Out' },
    { date: '25/10/24', value: 20152.12, month: 'Out' },
    { date: '28/10/24', value: 21024.70, month: 'Out' },
    { date: '29/10/24', value: 21240.92, month: 'Out' },
    { date: '30/10/24', value: 21570.07, month: 'Out' },
    { date: '31/10/24', value: 21862.91, month: 'Out' },
    { date: '01/11/24', value: 22095.23, month: 'Nov' },
    { date: '04/11/24', value: 22134.21, month: 'Nov' },
    { date: '05/11/24', value: 22388.05, month: 'Nov' },
    { date: '06/11/24', value: 22335.52, month: 'Nov' },
    { date: '07/11/24', value: 22390.20, month: 'Nov' },
    { date: '08/11/24', value: 22395.16, month: 'Nov' },
    { date: '11/11/24', value: 22513.58, month: 'Nov' },
    { date: '12/11/24', value: 23078.55, month: 'Nov' },
    { date: '13/11/24', value: 22807.14, month: 'Nov' },
    { date: '14/11/24', value: 23046.43, month: 'Nov' },
    { date: '18/11/24', value: 22825.92, month: 'Nov' },
    { date: '19/11/24', value: 22833.58, month: 'Nov' },
    { date: '20/11/24', value: 23153.40, month: 'Nov' },
    { date: '21/11/24', value: 23268.30, month: 'Nov' },
    { date: '22/11/24', value: 23154.79, month: 'Nov' },
    { date: '25/11/24', value: 23113.77, month: 'Nov' },
    { date: '26/11/24', value: 23351.73, month: 'Nov' },
    { date: '27/11/24', value: 23307.69, month: 'Nov' },
    { date: '28/11/24', value: 23453.02, month: 'Nov' },
    { date: '29/11/24', value: 23564.18, month: 'Nov' },
    { date: '02/12/24', value: 23535.90, month: 'Dez' },
    { date: '03/12/24', value: 24066.73, month: 'Dez' },
    { date: '04/12/24', value: 24295.55, month: 'Dez' },
    { date: '05/12/24', value: 23992.56, month: 'Dez' },
    { date: '06/12/24', value: 24131.89, month: 'Dez' },
    { date: '09/12/24', value: 24101.57, month: 'Dez' },
    { date: '10/12/24', value: 24374.10, month: 'Dez' },
    { date: '11/12/24', value: 24317.63, month: 'Dez' },
    { date: '12/12/24', value: 24396.72, month: 'Dez' },
    { date: '13/12/24', value: 24599.76, month: 'Dez' },
    { date: '16/12/24', value: 24874.95, month: 'Dez' },
    { date: '17/12/24', value: 24736.93, month: 'Dez' },
    { date: '18/12/24', value: 24772.07, month: 'Dez' },
    { date: '19/12/24', value: 24779.12, month: 'Dez' },
    { date: '20/12/24', value: 24750.49, month: 'Dez' },
    { date: '23/12/24', value: 25205.62, month: 'Dez' },
    { date: '24/12/24', value: 25388.66, month: 'Dez' },
    { date: '26/12/24', value: 25238.52, month: 'Dez' },
    { date: '27/12/24', value: 25524.13, month: 'Dez' },
    { date: '30/12/24', value: 26050.60, month: 'Dez' },
    { date: '31/12/24', value: 26359.14, month: 'Dez' },
    { date: '02/01/25', value: 26157.30, month: 'Jan' },
    { date: '03/01/25', value: 26162.49, month: 'Jan' },
    { date: '06/01/25', value: 26517.91, month: 'Jan' },
    { date: '07/01/25', value: 26478.42, month: 'Jan' },
    { date: '08/01/25', value: 26669.22, month: 'Jan' },
    { date: '09/01/25', value: 26926.19, month: 'Jan' },
    { date: '10/01/25', value: 26842.84, month: 'Jan' },
    { date: '13/01/25', value: 26932.98, month: 'Jan' },
    { date: '14/01/25', value: 26386.76, month: 'Jan' },
    { date: '15/01/25', value: 26283.92, month: 'Jan' },
    { date: '16/01/25', value: 26335.45, month: 'Jan' },
    { date: '17/01/25', value: 26187.93, month: 'Jan' },
    { date: '20/01/25', value: 26616.45, month: 'Jan' },
    { date: '21/01/25', value: 26432.46, month: 'Jan' },
    { date: '22/01/25', value: 26446.49, month: 'Jan' },
    { date: '23/01/25', value: 26574.68, month: 'Jan' },
    { date: '24/01/25', value: 26964.98, month: 'Jan' },
    { date: '27/01/25', value: 26779.84, month: 'Jan' },
    { date: '28/01/25', value: 27114.51, month: 'Jan' },
    { date: '29/01/25', value: 27218.60, month: 'Jan' },
    { date: '30/01/25', value: 27124.34, month: 'Jan' },
    { date: '31/01/25', value: 27318.80, month: 'Jan' },
    { date: '03/02/25', value: 27460.65, month: 'Fev' },
    { date: '04/02/25', value: 27442.64, month: 'Fev' },
    { date: '05/02/25', value: 27558.64, month: 'Fev' },
    { date: '06/02/25', value: 27583.62, month: 'Fev' },
    { date: '07/02/25', value: 27708.36, month: 'Fev' },
    { date: '10/02/25', value: 27942.83, month: 'Fev' },
    { date: '11/02/25', value: 28362.07, month: 'Fev' },
    { date: '12/02/25', value: 28216.55, month: 'Fev' },
    { date: '13/02/25', value: 28745.19, month: 'Fev' },
    { date: '14/02/25', value: 28456.82, month: 'Fev' },
    { date: '17/02/25', value: 28528.50, month: 'Fev' },
    { date: '18/02/25', value: 28748.20, month: 'Fev' },
    { date: '19/02/25', value: 28906.44, month: 'Fev' },
    { date: '20/02/25', value: 28883.94, month: 'Fev' },
    { date: '21/02/25', value: 28944.35, month: 'Fev' },
    { date: '24/02/25', value: 28947.79, month: 'Fev' },
    { date: '25/02/25', value: 28931.96, month: 'Fev' },
    { date: '26/02/25', value: 29203.92, month: 'Fev' },
    { date: '27/02/25', value: 29377.36, month: 'Fev' },
    { date: '28/02/25', value: 29340.82, month: 'Fev' },
    { date: '05/03/25', value: 29622.78, month: 'Mar' },
    { date: '06/03/25', value: 29786.27, month: 'Mar' },
    { date: '07/03/25', value: 30050.89, month: 'Mar' },
    { date: '10/03/25', value: 30278.85, month: 'Mar' },
    { date: '11/03/25', value: 30215.09, month: 'Mar' },
    { date: '12/03/25', value: 30205.09, month: 'Mar' },
    { date: '13/03/25', value: 30456.59, month: 'Mar' },
    { date: '14/03/25', value: 30680.71, month: 'Mar' },
    { date: '17/03/25', value: 30778.56, month: 'Mar' },
    { date: '18/03/25', value: 30904.07, month: 'Mar' },
    { date: '19/03/25', value: 31261.64, month: 'Mar' },
    { date: '20/03/25', value: 31245.37, month: 'Mar' },
    { date: '21/03/25', value: 31456.83, month: 'Mar' },
    { date: '24/03/25', value: 31518.43, month: 'Mar' },
    { date: '25/03/25', value: 31576.93, month: 'Mar' },
    { date: '26/03/25', value: 31898.72, month: 'Mar' },
    { date: '27/03/25', value: 32165.85, month: 'Mar' },
    { date: '28/03/25', value: 32430.59, month: 'Mar' },
    { date: '31/03/25', value: 32793.72, month: 'Mar' },
    { date: '01/04/25', value: 32899.96, month: 'Abr' },
    { date: '02/04/25', value: 33138.39, month: 'Abr' },
    { date: '03/04/25', value: 33178.38, month: 'Abr' },
    { date: '04/04/25', value: 33345.25, month: 'Abr' },
    { date: '07/04/25', value: 33421.26, month: 'Abr' },
    { date: '08/04/25', value: 33542.70, month: 'Abr' },
    { date: '09/04/25', value: 33763.77, month: 'Abr' },
    { date: '10/04/25', value: 33702.16, month: 'Abr' },
    { date: '11/04/25', value: 34222.68, month: 'Abr' },
    { date: '14/04/25', value: 34123.52, month: 'Abr' },
    { date: '15/04/25', value: 33982.72, month: 'Abr' },
    { date: '16/04/25', value: 34316.38, month: 'Abr' },
    { date: '17/04/25', value: 34576.75, month: 'Abr' },
    { date: '22/04/25', value: 34803.61, month: 'Abr' },
    { date: '23/04/25', value: 35031.32, month: 'Abr' },
    { date: '24/04/25', value: 35130.91, month: 'Abr' },
    { date: '25/04/25', value: 35053.50, month: 'Abr' },
    { date: '28/04/25', value: 35170.70, month: 'Abr' },
    { date: '29/04/25', value: 35137.31, month: 'Abr' },
    { date: '30/04/25', value: 35434.37, month: 'Abr' },
    { date: '02/05/25', value: 35507.00, month: 'Mai' },
    { date: '05/05/25', value: 35443.94, month: 'Mai' },
    { date: '06/05/25', value: 35481.70, month: 'Mai' },
    { date: '07/05/25', value: 35666.33, month: 'Mai' },
    { date: '08/05/25', value: 35655.62, month: 'Mai' },
    { date: '09/05/25', value: 35593.22, month: 'Mai' },
    { date: '12/05/25', value: 35743.99, month: 'Mai' },
    { date: '13/05/25', value: 35895.03, month: 'Mai' },
    { date: '14/05/25', value: 35895.68, month: 'Mai' },
    { date: '15/05/25', value: 35903.51, month: 'Mai' },
    { date: '16/05/25', value: 36051.96, month: 'Mai' },
    { date: '19/05/25', value: 35864.38, month: 'Mai' },
    { date: '20/05/25', value: 35684.93, month: 'Mai' },
    { date: '21/05/25', value: 35643.28, month: 'Mai' },
    { date: '22/05/25', value: 35702.63, month: 'Mai' },
    { date: '23/05/25', value: 35866.85, month: 'Mai' },
    { date: '26/05/25', value: 36263.96, month: 'Mai' },
    { date: '27/05/25', value: 36537.53, month: 'Mai' },
    { date: '28/05/25', value: 36607.58, month: 'Mai' },
    { date: '29/05/25', value: 36705.82, month: 'Mai' },
    { date: '30/05/25', value: 36607.35, month: 'Mai' },
    { date: '02/06/25', value: 36705.69, month: 'Jun' },
    { date: '03/06/25', value: 36749.99, month: 'Jun' },
    { date: '04/06/25', value: 36916.58, month: 'Jun' },
    { date: '05/06/25', value: 36853.17, month: 'Jun' },
    { date: '06/06/25', value: 37059.08, month: 'Jun' },
    { date: '09/06/25', value: 37467.66, month: 'Jun' },
    { date: '10/06/25', value: 37547.95, month: 'Jun' },
    { date: '11/06/25', value: 37730.33, month: 'Jun' },
    { date: '12/06/25', value: 37970.40, month: 'Jun' },
    { date: '13/06/25', value: 37992.20, month: 'Jun' },
    { date: '16/06/25', value: 38139.05, month: 'Jun' },
    { date: '17/06/25', value: 38243.61, month: 'Jun' },
    { date: '18/06/25', value: 38365.19, month: 'Jun' },
    { date: '20/06/25', value: 38312.62, month: 'Jun' },
    { date: '23/06/25', value: 38419.56, month: 'Jun' },
    { date: '24/06/25', value: 38621.20, month: 'Jun' }
  ];

  const initialValue = realPerformanceData[0].value;
  const finalValue = realPerformanceData[realPerformanceData.length - 1].value;
  const totalReturn = 193.11; // Provided value
  const maxValue = Math.max(...realPerformanceData.map(d => d.value));
  const minValue = Math.min(...realPerformanceData.map(d => d.value));
  const drawdownPercentage = 12.52; // Provided value
  const annualReturn = 107.24; // Provided value
  const monthlyReturn = 6.26; // Provided value

  // Right card new values
  const rightCardProfit = 52074;
  const rightCardRentability = 26.037;
  const rightCardDrawdown = 15.37;
  const rightCardAnnualCAGR = 175.12;
  const rightCardMonthlyCAGR = 10.72;

  const plans = [
    { 
      name: 'Mensal', 
      price: 'R$ 1.297', 
      period: '/mês',
      badge: null,
      savings: null 
    },
    { 
      name: 'Semestral', 
      price: 'R$ 997', 
      period: '/mês',
      badge: '23% OFF',
      savings: 'Economia de R$ 1.800' 
    },
    { 
      name: 'Anual', 
      price: 'R$ 897', 
      period: '/mês',
      badge: '31% OFF',
      savings: 'Economia de R$ 4.800',
      popular: true
    },
    { 
      name: 'Bienal', 
      price: 'R$ 697', 
      period: '/mês',
      badge: '46% OFF',
      savings: 'Economia de R$ 14.400' 
    },
  ];

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const dateParts = label.split('/');
      if (dateParts.length === 3) {
        const day = dateParts[0];
        const monthNumber = dateParts[1];
        const year = `20${dateParts[2]}`;
        
        const monthNames: { [key: string]: string } = {
          '01': 'Jan', '02': 'Fev', '03': 'Mar', '04': 'Abr', 
          '05': 'Mai', '06': 'Jun', '07': 'Jul', '08': 'Ago', 
          '09': 'Set', '10': 'Out', '11': 'Nov', '12': 'Dez'
        };
        
        const monthName = monthNames[monthNumber] || 'Jan';
        const monthYear = `${monthName}/${year}`;
        
        return (
          <div className="bg-slate-800 border border-slate-600 rounded-lg p-3 shadow-lg">
            <p className="text-slate-300 text-sm">{`Data: ${monthYear}`}</p>
            <p className="text-green-400 font-semibold">
              {`Resultado: R$ ${payload[0].value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
            </p>
          </div>
        );
      }
    }
    return null;
  };

  // Memoize expensive calculations
  const memoizedData = useMemo(() => realPerformanceData, []);
  const memoizedCustomTooltip = useCallback(CustomTooltip, []);

  return (
    <section id="copytrade" className="py-12 sm:py-16 lg:py-20 bg-slate-900 relative overflow-hidden">
      {/* Background Effect */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-400/5 via-transparent to-green-400/5" />
        <div className="absolute top-20 left-10 w-64 h-64 bg-cyan-400/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-green-400/5 rounded-full blur-3xl" />
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div 
          ref={headerAnimation.ref}
          className={`text-center mb-12 sm:mb-16 scroll-animate-scale ${
            headerAnimation.isVisible ? 'visible' : ''
          }`}
        >
          <Badge className="mb-4 bg-gradient-to-r from-cyan-400/10 to-green-400/10 border border-cyan-400/20 text-cyan-400">
            Copy Trade Inteligente
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">
            DUOCORE: <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-green-400">Dois Algoritmos, Uma Performance</span>
          </h2>
          <p className="text-lg sm:text-xl text-slate-300 max-w-3xl mx-auto">
            Sistema de copy trade que combina dois algoritmos quantitativos proprietários, 
            oferecendo diversificação automática e resultados consistentes.
          </p>
        </div>

        {/* Desktop Layout: Charts side by side */}
        <div 
          ref={benefitsAnimation.ref}
          id="resultados-duocore" 
          className={`hidden lg:block mb-12 sm:mb-16 scroll-animate ${benefitsAnimation.isVisible ? 'visible' : ''}`}
        >
          <div className="grid grid-cols-2 gap-8 mb-8">
            {/* Performance Chart */}
            <AnimatedCard 
              className="bg-slate-800/50 border-slate-700 flex flex-col" 
              hoverScale={true}
              glowEffect={true}
            >
              <CardHeader className="pb-4">
                <CardTitle className="text-white flex items-center space-x-2 text-lg sm:text-xl">
                  <BarChart3 className="h-5 w-5 text-cyan-400" />
                  <span>Performance DUOCORE | Jan/2024 à Jun/2025</span>
                  <Badge className="bg-slate-400/20 text-slate-300 border-slate-400/30 text-xs ml-2 whitespace-nowrap flex-shrink-0">
                    Margem: R$ 20k
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="flex-grow">
                <div className="space-y-6 h-full flex flex-col">
                  {/* Recharts Area Chart com borda verde e margens reduzidas */}
                  <div className="bg-slate-900/50 p-1 rounded-lg flex-grow border border-green-400/20" style={{ height: '320px' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={realPerformanceData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                        <defs>
                          <linearGradient id="performanceGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#22d3ee" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#10b981" stopOpacity={0.1}/>
                          </linearGradient>
                        </defs>
                        <XAxis 
                          dataKey="date" 
                          stroke="transparent"
                          fontSize={10}
                          axisLine={false}
                          tickLine={false}
                          tick={false}
                        />
                        <YAxis 
                          stroke="#9ca3af"
                          fontSize={9}
                          tickFormatter={(value) => `${Math.round(value / 1000)}k`}
                          axisLine={false}
                          tickLine={false}
                          width={30}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Area
                          type="monotone"
                          dataKey="value"
                          stroke="url(#gradient)"
                          fillOpacity={1}
                          fill="url(#performanceGradient)"
                          strokeWidth={3}
                        />
                        <defs>
                          <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#22d3ee" />
                            <stop offset="100%" stopColor="#10b981" />
                          </linearGradient>
                        </defs>
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Performance Information matching right card style */}
                  <div className="bg-gradient-to-r from-slate-900/80 to-slate-800/80 rounded-lg p-4 sm:p-6 border border-green-400/20">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-6">
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-slate-400 text-sm">Resultado Total:</span>
                          <span className="text-green-400 font-bold text-base sm:text-lg">R$ {finalValue.toLocaleString('pt-BR')}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-slate-400 text-sm">Período:</span>
                          <span className="text-white font-semibold">17 meses</span>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-slate-400 text-sm">Rentabilidade:</span>
                          <span className="text-green-400 font-bold text-base sm:text-lg">+{totalReturn}%</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-slate-400 text-sm">Drawdown Máx.:</span>
                          <span className="text-yellow-400 font-semibold">{drawdownPercentage}%</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-6 pt-6 border-t border-slate-700">
                      <div className="text-center">
                        <div className="text-green-400 font-bold text-xl sm:text-2xl mb-2">
                          Patrimônio em Crescimento Consistente
                        </div>
                        <div className="text-slate-400 text-sm mb-3">17 meses de Resultados verificados em conta real</div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="text-center">
                            <div className="text-cyan-400 font-bold text-base">+{annualReturn}%</div>
                            <div className="text-slate-400 text-xs">Rentab. Anual (CAGR)</div>
                          </div>
                          <div className="text-center">
                            <div className="text-green-400 font-bold text-base">+{monthlyReturn}%</div>
                            <div className="text-slate-400 text-xs">Rentab. Mensal (CAGR)</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </AnimatedCard>

            {/* Resultado Conta Real DUOCORE */}
            <AnimatedCard 
              className="bg-slate-800/50 border-slate-700 flex flex-col" 
              hoverScale={true}
              glowEffect={true}
            >
              <CardHeader className="pb-4">
                <CardTitle className="text-white flex items-center space-x-2 text-lg sm:text-xl">
                  <TrendingUp className="h-5 w-5 text-green-400" />
                  <span>Resultado em Conta Real DUOCORE</span>
                  <Badge className="bg-slate-400/20 text-slate-300 border-slate-400/30 text-xs ml-2 whitespace-nowrap flex-shrink-0">
                    Margem: R$ 200k
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="flex-grow">
                <div className="space-y-6 h-full flex flex-col">
                   {/* Container da imagem */}
                   <div className="bg-slate-900/50 rounded-lg flex-grow border border-green-400/20 overflow-hidden" style={{ height: '320px' }}>
                       <OptimizedImage 
                         src="/lovable-uploads/quantmake-grafico-resultado-duocore-trading-automatizado.png" 
                         alt="Resultado Real DUOCORE QuantMake.AI - Gráfico Performance Copy Trade Automatizado Tecnologia Dual-Core Trading Futuro B3 Margem 20k"
                         className="w-full h-full object-fill"
                         loading="lazy"
                       />
                  </div>

                  <div className="bg-gradient-to-r from-slate-900/80 to-slate-800/80 rounded-lg p-4 sm:p-6 border border-green-400/20">
                     <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-6">
                       <div className="space-y-3">
                         <div className="flex justify-between items-center">
                           <span className="text-slate-400 text-sm">Resultado Total:</span>
                           <span className="text-green-400 font-bold text-base sm:text-lg">R$ {rightCardProfit.toLocaleString('pt-BR')}</span>
                         </div>
                         <div className="flex justify-between items-center">
                           <span className="text-slate-400 text-sm">Período:</span>
                            <span className="text-white font-semibold">3 meses</span>
                         </div>
                       </div>
                       <div className="space-y-3">
                         <div className="flex justify-between items-center">
                           <span className="text-slate-400 text-sm">Rentabilidade:</span>
                           <span className="text-green-400 font-bold text-base sm:text-lg">+{rightCardRentability}%</span>
                         </div>
                         <div className="flex justify-between items-center">
                           <span className="text-slate-400 text-sm">Drawdown Máx.:</span>
                           <span className="text-yellow-400 font-semibold">{rightCardDrawdown}%</span>
                         </div>
                       </div>
                     </div>
                     
                     <div className="mt-6 pt-6 border-t border-slate-700">
                       <div className="text-center">
                         <div className="text-green-400 font-bold text-xl sm:text-2xl mb-2">
                            Crescimento Consistente em Conta Real
                         </div>
                           <div className="text-slate-400 text-sm mb-3">Resultados verificados em conta real</div>
                         <div className="grid grid-cols-2 gap-4">
                           <div className="text-center">
                             <div className="text-cyan-400 font-bold text-base">+{rightCardAnnualCAGR}%</div>
                             <div className="text-slate-400 text-xs">Rentab. Anual (CAGR)</div>
                           </div>
                           <div className="text-center">
                             <div className="text-green-400 font-bold text-base">+{rightCardMonthlyCAGR}%</div>
                             <div className="text-slate-400 text-xs">Rentab. Mensal (CAGR)</div>
                           </div>
                         </div>
                       </div>
                     </div>
                  </div>
                </div>
              </CardContent>
            </AnimatedCard>
          </div>

          {/* Information below charts */}
          <div className="space-y-8">
            {/* Como Funciona o DUOCORE - Improved UI */}
            <div id="como-funciona" className="bg-gradient-to-r from-slate-900/50 to-slate-800/50 rounded-lg p-6 sm:p-8 border border-cyan-400/20">
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-8 text-center">Como Funciona o DUOCORE</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-slate-800/50 rounded-lg p-6 border border-cyan-400/20 hover:border-cyan-400/40 transition-all duration-300">
                  <div className="flex flex-col items-center space-y-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-cyan-400/20 to-cyan-600/20 rounded-xl flex items-center justify-center">
                      <Layers className="h-8 w-8 text-cyan-400" />
                    </div>
                    <div className="text-center">
                      <h4 className="text-white font-bold text-lg mb-3">Dois Algoritmos, Uma Performance</h4>
                      <p className="text-slate-300 text-sm leading-relaxed">
                        Sinergia perfeita entre algoritmos sincronos, trabalhando em conjunto para maximizar oportunidades em qualquer cenário de mercado.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800/50 rounded-lg p-6 border border-green-400/20 hover:border-green-400/40 transition-all duration-300">
                  <div className="flex flex-col items-center space-y-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-green-400/20 to-green-600/20 rounded-xl flex items-center justify-center">
                      <Play className="h-8 w-8 text-green-400" />
                    </div>
                    <div className="text-center">
                      <h4 className="text-white font-bold text-lg mb-3">Tecnologia Plug & Play</h4>
                      <p className="text-slate-300 text-sm leading-relaxed">
                        Configuração simplificada: você dá o play e nossa tecnologia de automação cuida de todo o trabalho pesado do trading para você.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800/50 rounded-lg p-6 border border-yellow-400/20 hover:border-yellow-400/40 transition-all duration-300">
                  <div className="flex flex-col items-center space-y-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-yellow-400/20 to-yellow-600/20 rounded-xl flex items-center justify-center">
                      <Shield className="h-8 w-8 text-yellow-400" />
                    </div>
                    <div className="text-center">
                      <h4 className="text-white font-bold text-lg mb-3">Gestão de Risco Inteligente</h4>
                      <p className="text-slate-300 text-sm leading-relaxed">
                        Sistema avançado de controle de exposição e diversificação automática de risco para máxima proteção.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Stats Section */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-8 pt-8 border-t border-slate-700">
                <div className="text-center p-6 bg-gradient-to-br from-cyan-400/10 to-cyan-600/5 rounded-lg border border-cyan-400/20">
                  <DollarSign className="h-10 w-10 text-cyan-400 mx-auto mb-4" />
                  <div className="text-3xl font-bold text-white mb-2">R$ 20k</div>
                  <div className="text-sm text-slate-400">Margem Mínima</div>
                </div>
                <div className="text-center p-6 bg-gradient-to-br from-green-400/10 to-green-600/5 rounded-lg border border-green-400/20">
                  <Layers className="h-10 w-10 text-green-400 mx-auto mb-4" />
                  <div className="text-3xl font-bold text-white mb-2">Multi-Core</div>
                  <div className="text-sm text-slate-400">Múltiplos Cores Simultâneos</div>
                </div>
                <div className="text-center p-6 bg-gradient-to-br from-yellow-400/10 to-yellow-600/5 rounded-lg border border-yellow-400/20">
                  <Building2 className="h-10 w-10 text-yellow-400 mx-auto mb-4" />
                  <div className="text-3xl font-bold text-white mb-2">B3</div>
                  <div className="text-sm text-slate-400">Mercado Futuro B3</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Layout: Original layout for mobile */}
        <div className="lg:hidden flex flex-col gap-8 items-start mb-12 sm:mb-16">
          {/* Performance Chart - Full width on mobile */}
          <div className="w-full space-y-6">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="pb-4">
                <CardTitle className="text-white flex items-center space-x-2 text-lg sm:text-xl">
                  <BarChart3 className="h-5 w-5 text-cyan-400" />
                  <span>Performance DUOCORE | Jan/2024 à Jun/2025</span>
                  <Badge className="bg-slate-400/20 text-slate-300 border-slate-400/30 text-xs ml-2 whitespace-nowrap flex-shrink-0">
                    Margem: R$ 20k
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Recharts Area Chart */}
                  <div className="bg-slate-900/50 p-4 sm:p-6 rounded-lg h-64 sm:h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={realPerformanceData}>
                        <defs>
                          <linearGradient id="performanceGradientMobile" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#22d3ee" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#10b981" stopOpacity={0.1}/>
                          </linearGradient>
                        </defs>
                        <XAxis 
                          dataKey="date" 
                          stroke="transparent"
                          fontSize={12}
                          axisLine={false}
                          tickLine={false}
                          tick={false}
                        />
                        <YAxis 
                          stroke="#9ca3af"
                          fontSize={12}
                          tickFormatter={(value) => `R$ ${(value / 1000).toFixed(0)}k`}
                          axisLine={false}
                          tickLine={false}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Area
                          type="monotone"
                          dataKey="value"
                          stroke="url(#gradientMobile)"
                          fillOpacity={1}
                          fill="url(#performanceGradientMobile)"
                          strokeWidth={3}
                        />
                        <defs>
                          <linearGradient id="gradientMobile" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#22d3ee" />
                            <stop offset="100%" stopColor="#10b981" />
                          </linearGradient>
                        </defs>
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                  
                   <div className="bg-gradient-to-r from-slate-900/80 to-slate-800/80 rounded-lg p-4 sm:p-6 border border-green-400/20">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-6">
                        <div className="space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="text-slate-400 text-sm">Resultado Total:</span>
                            <span className="text-cyan-400 font-bold text-base sm:text-lg">R$ {finalValue.toLocaleString('pt-BR')}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-slate-400 text-sm">Período:</span>
                            <span className="text-white font-semibold">17 meses</span>
                          </div>
                        </div>
                        <div className="space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="text-slate-400 text-sm">Rentabilidade:</span>
                            <span className="text-green-400 font-bold text-base sm:text-lg">+{totalReturn}%</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-slate-400 text-sm">Drawdown Máx.:</span>
                            <span className="text-yellow-400 font-semibold">{drawdownPercentage}%</span>
                          </div>
                        </div>
                      </div>
                     
                     <div className="mt-6 pt-6 border-t border-slate-700">
                       <div className="text-center">
                         <div className="text-green-400 font-bold text-xl sm:text-2xl mb-2">
                           Crescimento Consistente Comprovado
                         </div>
                         <div className="text-slate-400 text-sm mb-3">performance algoritmos sincronizados</div>
                         <div className="grid grid-cols-2 gap-4">
                           <div className="text-center">
                             <div className="text-cyan-400 font-bold text-base">+{annualReturn}%</div>
                             <div className="text-slate-400 text-xs">Rentab. Anual (CAGR)</div>
                           </div>
                           <div className="text-center">
                             <div className="text-green-400 font-bold text-base">+{monthlyReturn}%</div>
                             <div className="text-slate-400 text-xs">Rentab. Mensal (CAGR)</div>
                           </div>
                         </div>
                       </div>
                     </div>
                   </div>
                </div>
              </CardContent>
            </Card>

            {/* Resultado Conta Real DUOCORE */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="pb-4">
                <CardTitle className="text-white flex items-center space-x-2 text-lg sm:text-xl">
                  <TrendingUp className="h-5 w-5 text-green-400" />
                  <span>Resultado em Conta Real DUOCORE</span>
                  <Badge className="bg-slate-400/20 text-slate-300 border-slate-400/30 text-xs ml-2 whitespace-nowrap flex-shrink-0">
                    Margem: R$ 200k
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Container da imagem mobile */}
                 <div className="bg-slate-900/50 rounded-lg border border-green-400/20 overflow-hidden mb-6" style={{ height: '280px' }}>
                     <img 
                       src="/lovable-uploads/quantmake-grafico-resultado-duocore-trading-automatizado.png" 
                       alt="Resultado Real DUOCORE QuantMake.AI - Gráfico Performance Copy Trade Automatizado Tecnologia Dual-Core Trading Futuro B3 Margem 20k"
                       className="w-full h-full object-fill"
                       style={{ objectPosition: 'left top' }}
                     />
                 </div>

                 <div className="bg-gradient-to-r from-slate-900/80 to-slate-800/80 rounded-lg p-4 sm:p-6 border border-green-400/20">
                   <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-6">
                     <div className="space-y-3">
                       <div className="flex justify-between items-center">
                         <span className="text-slate-400 text-sm">Resultado Total:</span>
                         <span className="text-green-400 font-bold text-base sm:text-lg">R$ {rightCardProfit.toLocaleString('pt-BR')}</span>
                       </div>
                       <div className="flex justify-between items-center">
                         <span className="text-slate-400 text-sm">Período:</span>
                          <span className="text-white font-semibold">3 meses</span>
                       </div>
                     </div>
                     <div className="space-y-3">
                       <div className="flex justify-between items-center">
                         <span className="text-slate-400 text-sm">Rentabilidade:</span>
                         <span className="text-green-400 font-bold text-base sm:text-lg">+{rightCardRentability}%</span>
                       </div>
                       <div className="flex justify-between items-center">
                         <span className="text-slate-400 text-sm">Drawdown Máx.:</span>
                         <span className="text-yellow-400 font-semibold">{rightCardDrawdown}%</span>
                       </div>
                     </div>
                   </div>
                   
                   <div className="mt-6 pt-6 border-t border-slate-700">
                     <div className="text-center">
                       <div className="text-green-400 font-bold text-xl sm:text-2xl mb-2">
                          Crescimento Consistente em Conta Real
                       </div>
                        <div className="text-slate-400 text-sm mb-3">Resultados verificados em conta real</div>
                       <div className="grid grid-cols-2 gap-4">
                         <div className="text-center">
                           <div className="text-cyan-400 font-bold text-base">+{rightCardAnnualCAGR}%</div>
                           <div className="text-slate-400 text-xs">Rentab. Anual (CAGR)</div>
                         </div>
                         <div className="text-center">
                           <div className="text-green-400 font-bold text-base">+{rightCardMonthlyCAGR}%</div>
                           <div className="text-slate-400 text-xs">Rentab. Mensal (CAGR)</div>
                         </div>
                        </div>
                      </div>
                    </div>
                  </div>
              </CardContent>
            </Card>

          </div>
        </div>

        {/* Pricing Plans */}
        <div id="planos-duocore" className="mb-12 sm:mb-16">
          <h3 
            ref={plansHeaderAnimation.ref}
            className={`text-2xl sm:text-3xl font-bold text-white text-center mb-8 scroll-animate scroll-animate-scale ${plansHeaderAnimation.isVisible ? 'visible' : ''}`}
          >
            Planos <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-green-400">DUOCORE</span>
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-4 lg:gap-6">
            {plans.map((plan, index) => (
              <Card 
                key={index} 
                className={`bg-slate-800/50 border-slate-700 hover:border-cyan-400/50 transition-all duration-300 relative h-full flex flex-col scroll-animate stagger-${index + 1} ${plansHeaderAnimation.isVisible ? 'visible' : ''} ${plan.popular ? 'border-cyan-400 scale-100 sm:scale-105' : ''}`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                    <Badge className="bg-gradient-to-r from-cyan-400 to-green-400 text-slate-900 font-bold text-xs px-3 py-1 whitespace-nowrap rounded-full">
                      MAIS POPULAR
                    </Badge>
                  </div>
                )}
                <CardHeader className="text-center flex-grow pb-4">
                  <div className="flex items-center justify-center space-x-2 mb-2">
                    <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-cyan-400" />
                    <CardTitle className="text-lg sm:text-xl text-white">{plan.name}</CardTitle>
                  </div>
                  <div className="flex items-baseline justify-center space-x-1 whitespace-nowrap">
                    <span className="text-2xl sm:text-3xl font-bold text-white">{plan.price}</span>
                    <span className="text-slate-400 text-sm">{plan.period}</span>
                  </div>
                  {plan.badge && (
                    <div className="mt-2 mb-3">
                      <div className="relative bg-slate-700/50 rounded-full h-6 overflow-hidden">
                        <div 
                          className="absolute top-0 left-0 h-full bg-gradient-to-r from-green-400 to-green-500 transition-all duration-500"
                          style={{ width: `${plan.badge.match(/(\d+)%/)?.[1] || '0'}%` }}
                        ></div>
                         <div className="relative z-10 text-center text-white text-xs font-medium py-1 leading-4">
                           {plan.badge.match(/(\d+)%/)?.[1]}% OFF
                         </div>
                      </div>
                      {plan.savings && (
                        <div className="text-center text-slate-400 text-xs mt-1">
                          {plan.savings}
                        </div>
                      )}
                    </div>
                  )}
                </CardHeader>
                <CardContent className="mt-auto pt-0">
                  <Button 
                    onClick={() => handlePlanSelect(`Plano ${plan.name}`)}
                    className={`w-full font-semibold text-sm transition-all duration-300 ${
                      plan.popular 
                        ? 'bg-gradient-to-r from-cyan-400 to-green-400 hover:from-cyan-500 hover:to-green-500 text-slate-900' 
                        : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 border border-blue-400/30 hover:border-blue-400/50'
                    }`}
                  >
                    ASSINAR {plan.name.toUpperCase()}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      <WaitlistModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        planName={selectedPlan}
      />
    </section>
  );
});

CopyTradeSection.displayName = 'CopyTradeSection';

export default CopyTradeSection;
